import React, { useMemo, useState, useEffect, useRef } from 'react';
import { formatTimeShort, formatRecurrenceLabel, normalizeRecurrence, computeNextOccurrence, formatMonthDay } from '../lib/reminderParser';
import { hashReminderLine } from '../hooks/useReminders';
import { readListAt } from '../hooks/useData';
import { useOverdueHiddenUntil, setOverdueHiddenUntil, tomorrowStart } from '../lib/overdueHide';
import { snoozeToDays, formatSnoozeLabel, REMINDER_TOAST_TTL_MS } from '../lib/snoozeDate';
import ReminderEditPopover from './ReminderEditPopover';
import ModalCloseButton from './ModalCloseButton';
import HighlightedText from './HighlightedText';
import {
  setAgendaRows, clearAgendaRows, registerAgendaRowEl, subscribeAgendaFind, scrollAgendaRowIntoView,
} from '../lib/agendaFind';

const PREFIX = '🔔 ';
/**
 * AgendaPanel — full-height slide-in panel that aggregates every
 * reminder for the user across all boards/lists.
 *
 * Sections (in order):
 *   - Overdue (anything dated in the past, status = open)
 *   - Today
 *   - Tomorrow
 *   - This Week (next 2-7 days)
 *   - Later (8+ days out)
 *   - No Date (undated reminders — designated-list freeform paragraphs)
 *
 * Per-row actions (right-side menu):
 *   - snooze: bump the trigger date forward 1 day
 *   - mark done: status -> 'done', greyed out (still visible until next
 *     mount — keeps the user feeling like they actually crossed it off
 *     before it disappears)
 *   - delete: hard delete the doc
 *   - open: jump to the source list/line
 *
 * Inline edit:
 *   - Click the date chip to swap in a date+time picker. Saves on blur
 *     or Enter. The `text` field is also editable inline (click to
 *     focus, blur to save).
 */
export default function AgendaPanel({
  open,
  onClose,
  // Walter 2026-05-11: when set (by BoardPage's openReminderEditor),
  // the agenda mounts with the structured edit popover already open
  // for that reminder. Consumed once via onConsumePendingEdit so the
  // popover doesn't reopen on every subsequent open.
  pendingEditReminderId = null,
  onConsumePendingEdit,
  // Walter 2026-10-09: the reminder his cursor is on RIGHT NOW - scroll to it
  // and keep it marked, so he doesn't have to hunt for the row he was just
  // looking at. Null when the cursor isn't on a reminder line; the mark then
  // goes away. (First cut flashed for 1.8s; he asked for it to stay.)
  focusReminderId = null,
  reminders = [],
  updateReminder,
  // Two flavours of "remove":
  //   deactivate  — strips the bell prefix from the source line and
  //                 deletes the reminder doc. The line text stays.
  //                 Default action — least destructive.
  //   delete      — removes the source line entirely AND deletes
  //                 the doc. Real trash.
  // Both push undo toasts.
  deactivateReminder,
  deleteReminder,
  // (reminder, newNotesText) => void — called when the user edits the
  // notes for a reminder. Caller writes back to the source list.
  updateNotes,
  // The user's lists on this board (passed in so the agenda can show
  // each reminder's attached notes — the continuation lines that
  // follow the 🔔 line in the source). For cross-board reminders we
  // resolve via the readListAt one-shot fetch below.
  lists = [],
  onJumpToReminder, // (reminder) => void — caller resolves source list
  // Walter 2026-05-13: "+ new" button in the agenda header. Caller
  // resolves the source list (auto-picks the user's first designated
  // reminder list) and surfaces a toast if none exists. Signature:
  // (anchorRect) => void — the click handler in AgendaPanel sends
  // the button's bounding rect so the popover can anchor to it.
  onRequestNewReminder,
  // Walter 2026-05-06 round 4: needed so the popover can resolve a
  // cross-board source list (find which board the source lives on,
  // then fetch the doc directly via readListAt). Without these the
  // popover's notes textarea + source preview were empty for any
  // reminder whose source list wasn't on the active board.
  currentUid,
  listIdToBoardId = {},
  // Walter 2026-05-06 round 6: undo toasts on every reminder action
  // that mutates state (mark-done, snooze-1d, bulk push-1wk, bulk
  // make-undated). pushToast(message, undoAction) — undoAction is
  // the callback that reverses the change.
  pushToast,
  // Walter 2026-09-14: his default reminder time ("HH:MM"), passed on to
  // the edit popover so a date typed without a time resolves the same way
  // it would typed into a list (lib/resolveReminder).
  defaultReminderTime,
}) {
  const [filter, setFilter] = useState('all'); // 'all' | 'today' | 'week'
  // Walter 2026-05-05: clicking the "source" RowAction now opens an
  // inline editor popover anchored to the row, instead of navigating
  // to the source list. We track which reminder is being edited and
  // the row's bounding rect so the popover can position itself.
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [editingAnchorRect, setEditingAnchorRect] = useState(null);
  const editingReminder = editingReminderId
    ? reminders.find(r => r.id === editingReminderId)
    : null;
  const localSourceList = editingReminder
    ? lists.find(l => l.id === editingReminder.sourceListId) || null
    : null;
  // Cross-board fallback: when the popover opens for a reminder
  // whose source list isn't in the active board's `lists`, fetch
  // the list doc directly so the popover can hydrate notes +
  // source preview. Cleared when the popover closes or the user
  // switches reminders. Walter 2026-05-06 round 4.
  const [editingSourceListFetched, setEditingSourceListFetched] = useState(null);
  useEffect(() => {
    if (!editingReminder || localSourceList || !currentUid) {
      setEditingSourceListFetched(null);
      return;
    }
    const targetBoardId = editingReminder.sourceBoardId
      || (listIdToBoardId && listIdToBoardId[editingReminder.sourceListId])
      || null;
    if (!targetBoardId || !editingReminder.sourceListId) {
      setEditingSourceListFetched(null);
      return;
    }
    let cancelled = false;
    readListAt(currentUid, targetBoardId, editingReminder.sourceListId).then(sl => {
      if (!cancelled) setEditingSourceListFetched(sl);
    }).catch(() => {
      if (!cancelled) setEditingSourceListFetched(null);
    });
    return () => { cancelled = true; };
  }, [editingReminderId, editingReminder?.sourceListId, editingReminder?.sourceBoardId, localSourceList, currentUid, listIdToBoardId, editingReminder]);
  const editingSourceList = localSourceList || editingSourceListFetched;

  // Lock body scroll while panel is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Walter 2026-05-11: when BoardPage passes pendingEditReminderId
  // (after a 🔁-click in a source list), open the structured edit
  // popover for that reminder immediately. No anchor rect → the
  // popover positions itself in the centered/mobile layout.
  useEffect(() => {
    if (!open || !pendingEditReminderId) return;
    setEditingReminderId(pendingEditReminderId);
    setEditingAnchorRect(null);
    onConsumePendingEdit?.();
  }, [open, pendingEditReminderId, onConsumePendingEdit]);

  // Walter 2026-10-09: open AT the reminder his cursor is on, and KEEP it
  // marked while it stays there - "indefinitely and only if I'm on that line
  // right now". Scroll only, no popover: he asked to SEE the item, and opening
  // its editor every time would be in the way. The scroll fires when the
  // target CHANGES, not on every render, so it never fights his scrolling.
  // Nothing happens when a filter tab hides the row or the reminder is gone.
  // rAF so the rows have mounted and registered their nodes.
  useEffect(() => {
    if (!open || !focusReminderId) return undefined;
    const raf = requestAnimationFrame(() => scrollAgendaRowIntoView(focusReminderId));
    return () => cancelAnimationFrame(raf);
  }, [open, focusReminderId]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Walter 2026-05-06 round 6: while overdue is hidden-until-
  // tomorrow, every overdue reminder is treated as if it doesn't
  // exist for the agenda's purposes (sections + bell-badge math
  // upstream). Recomputes once per minute so the hide expires at
  // its target time without a manual refresh.
  const overdueHiddenUntil = useOverdueHiddenUntil();
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!overdueHiddenUntil) return undefined;
    const id = setInterval(() => forceTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, [overdueHiddenUntil]);
  const overdueHiddenActive = overdueHiddenUntil > Date.now();
  const visibleReminders = useMemo(() => {
    if (!overdueHiddenActive) return reminders;
    const now = new Date();
    return reminders.filter(r => {
      if (r.status === 'done') return true;
      const d = fireDate(r.parsedDate);
      if (!d) return true;
      return d >= now;
    });
  }, [reminders, overdueHiddenActive]);
  const sections = useMemo(() => groupReminders(visibleReminders, filter), [visibleReminders, filter]);

  // Ctrl+F searches the agenda (Walter 2026-08-23: "search doesn't work on
  // this mode"). Publish the rows that are ACTUALLY ON SCREEN, in display
  // order — his call for v1: the current filter tab is the scope, so a hit
  // hiding behind "overdue"/"today" isn't counted. Same principle as the rest
  // of find: you search what you can see.
  useEffect(() => {
    const flat = [];
    sections.forEach(sec => sec.items.forEach(r => {
      flat.push({
        id: r.id,
        text: r.text || '',
        // Notes are searched from the DATA, never from the open textarea —
        // one source, so a note can't be counted twice depending on whether
        // its row happens to be expanded.
        notes: joinNotes(lookupNotesForReminder(r, lists)),
      });
    }));
    setAgendaRows(flat);
    return () => clearAgendaRows();
  }, [sections, lists]);

  // Triage state — when the user clicks "process them" we step through
  // overdue items one at a time. Index resets when the panel closes
  // OR when the overdue list is exhausted.
  const [triageIdx, setTriageIdx] = useState(-1);
  // Overdue items computed up-front so the triage modal and the banner
  // see the same list across renders. We pull from the unfiltered
  // reminders so the triage walkthrough doesn't get cut short by the
  // user's "today" or "this week" filter.
  const overdueAll = useMemo(() => {
    const now = new Date();
    return reminders
      .filter(r => r.status !== 'done')
      .filter(r => {
        const d = fireDate(r.parsedDate);
        return d && d < now;
      })
      .sort((a, b) => fireDate(a.parsedDate) - fireDate(b.parsedDate));
  }, [reminders]);

  if (!open) return null;

  const totalCount = reminders.filter(r => r.status !== 'done').length;
  const undatedCount = reminders.filter(r => !r.parsedDate && r.status !== 'done').length;
  const overdueCount = overdueAll.length;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        // Walter 2026-05-13: softer dim. The 0.4 black overlay made
        // the source list visible to the left of the agenda look
        // brownish/muddy on light theme — the cream surface mixed
        // with 40% black mud-tone. 0.2 still reads as "modal is
        // open" without warping the underlying colors.
        background: 'rgba(0,0,0,0.2)',
        zIndex: 2200,
        display: 'flex', justifyContent: 'flex-end',
        animation: 'lists-fadein 0.15s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        // While the agenda is open it IS the search scope — the board's lists
        // stay mounted behind it and would otherwise contribute hits nobody
        // can see. Same mechanism as a full-screen list (#194).
        data-find-scope="agenda"
        style={{
          width: 'min(560px, 100%)',
          height: '100%',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--border-bright)',
          boxShadow: 'var(--shadow-panel-edge)',
          display: 'flex', flexDirection: 'column',
          fontFamily: 'var(--mono)',
          color: 'var(--text)',
          // Position relative so the triage walkthrough overlay
          // (position: absolute, inset: 0) covers just this panel,
          // not the whole-screen backdrop.
          position: 'relative',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: '12px',
        }}>
          <span style={{ color: 'var(--accent)' }}>🔔</span>
          <span style={{ fontSize: 'calc(13px * var(--ui-scale, 1))', fontWeight: 600 }}>
            reminders
          </span>
          <span style={{ color: 'var(--text-dimmer)', fontSize: 'calc(11px * var(--ui-scale, 1))' }}>
            {totalCount} active{undatedCount ? ` · ${undatedCount} no date` : ''}
            {overdueCount ? ` · ${overdueCount} overdue` : ''}
          </span>
          <span style={{ flex: 1 }} />
          {/* "+ new" — Walter 2026-05-13: launches the structured
              reminder editor in create mode. BoardPage resolves the
              source list (user's first designated reminder list,
              auto-pick) and surfaces a toast if none exists. */}
          {onRequestNewReminder && (
            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                onRequestNewReminder(rect);
              }}
              title="new reminder"
              aria-label="new reminder"
              // Walter 2026-09-14: "New is kind of hard to spot. why not
              // orange." A grey outline next to the close button read as
              // chrome, not as the primary action it is. Now the same solid
              // accent as the board header's "+ new".
              style={{
                background: 'var(--accent)',
                color: 'var(--bg)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius)',
                padding: '4px 10px 4px 8px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              new
            </button>
          )}
          <ModalCloseButton onClick={onClose} style={{ position: 'static' }} />
        </div>

        {/* Filter bar */}
        <div style={{
          padding: '10px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', gap: '6px',
          alignItems: 'center',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
        }}>
          {[
            { id: 'all',     label: 'all' },
            { id: 'overdue', label: 'overdue' },
            { id: 'today',   label: 'today' },
            { id: 'week',    label: 'this week' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                background: filter === f.id ? 'var(--accent-wash)' : 'transparent',
                color: filter === f.id ? 'var(--accent)' : 'var(--text-dim)',
                border: '1px solid ' + (filter === f.id ? 'var(--accent-dim)' : 'var(--border)'),
                borderRadius: 'var(--radius)',
                padding: '4px 10px',
                fontFamily: 'var(--mono)',
                fontSize: 'inherit',
                cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
          {/* Walter 2026-05-21: removed the "organise" button from the
              agenda. The agenda view already presents reminders in
              sorted sections — an organise button here was confusing
              ("it is already organised"). The organise action still
              lives on the source-list tile header (onTriggerOrganize
              in ListPanel), which is where rewriting the raw list text
              into sections actually belongs. */}
        </div>

        {/* Overdue triage banner — appears whenever there's at least
            one overdue item. Spec said >3 but early-beta usability
            wins by surfacing the walkthrough sooner; we can re-tune
            the threshold once the panel feels lived-in. */}
        {/* Banner is suppressed while overdue is hidden — replaced
            below by a subtle reveal line. */}
        {overdueCount >= 1 && triageIdx < 0 && !overdueHiddenActive && (
          <OverdueTriageBanner
            count={overdueCount}
            onProcess={() => setTriageIdx(0)}
            // Walter 2026-05-06 round 6: replaced "snooze all 1d"
            // (which mutated dates) with "hide until tomorrow"
            // (visual-only suppression). Walter's framing: he wants
            // overdue items to go away for today without conflating
            // them with stuff he actually plans to do tomorrow.
            onHideUntilTomorrow={() => {
              const prev = overdueHiddenUntil;
              setOverdueHiddenUntil(tomorrowStart());
              pushToast?.('overdue items hidden until tomorrow', () => setOverdueHiddenUntil(prev || 0));
            }}
            onPushAllOneWeek={() => {
              // Snapshot the originals so undo can restore each
              // reminder to its prior date in one batch.
              const snapshot = overdueAll.map(r => ({ id: r.id, prev: fireDate(r.parsedDate) || null }));
              for (const r of overdueAll) {
                const d = fireDate(r.parsedDate) || new Date();
                const next = new Date(d);
                next.setDate(next.getDate() + 7);
                updateReminder?.(r.id, { parsedDate: next });
              }
              pushToast?.(
                `pushed ${snapshot.length} overdue forward 1 week`,
                () => { for (const s of snapshot) updateReminder?.(s.id, { parsedDate: s.prev }); },
              );
            }}
            onMakeAllUndated={() => {
              const snapshot = overdueAll.map(r => ({ id: r.id, prev: fireDate(r.parsedDate) || null }));
              for (const r of overdueAll) {
                updateReminder?.(r.id, { parsedDate: null });
              }
              pushToast?.(
                `cleared dates on ${snapshot.length} overdue`,
                () => { for (const s of snapshot) updateReminder?.(s.id, { parsedDate: s.prev }); },
              );
            }}
          />
        )}
        {overdueHiddenActive && overdueCount >= 1 && (
          <div style={{
            padding: '8px 18px',
            borderBottom: '1px solid var(--border)',
            background: 'transparent',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            color: 'var(--text-dimmer)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontStyle: 'italic' }}>
              {overdueCount} overdue item{overdueCount === 1 ? '' : 's'} hidden until tomorrow
            </span>
            <span style={{ color: 'var(--text-dimmer)' }}>·</span>
            <button
              onClick={() => setOverdueHiddenUntil(0)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent)',
                fontFamily: 'var(--mono)',
                fontSize: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              show now
            </button>
          </div>
        )}

        {/* Body — sections */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {sections.length === 0 || sections.every(s => s.items.length === 0) ? (
            <div style={{
              padding: '40px 18px',
              textAlign: 'center',
              color: 'var(--text-dimmer)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              lineHeight: 1.6,
            }}>
              No reminders yet. Type something like
              {' '}<span style={{ color: 'var(--accent)' }}>tomorrow 3pm dentist</span>{' '}
              in any list to make one.
            </div>
          ) : sections.map(section => (
            section.items.length > 0 && (
              <Section key={section.id} title={section.label} accent={section.accent}>
                {section.items.map(r => (
                  <ReminderRow
                    key={r.id}
                    reminder={r}
                    atCaret={focusReminderId === r.id}
                    notes={lookupNotesForReminder(r, lists)}
                    updateReminder={updateReminder}
                    deactivateReminder={deactivateReminder}
                    deleteReminder={deleteReminder}
                    updateNotes={updateNotes}
                    pushToast={pushToast}
                    onOpenSourceEditor={(rect) => {
                      setEditingAnchorRect(rect);
                      setEditingReminderId(r.id);
                    }}
                  />
                ))}
              </Section>
            )
          ))}
        </div>

        {/* Triage walkthrough — full-cover overlay inside the panel.
            Steps through overdue items one at a time with quick
            actions per item. Closes when out of items or user exits. */}
        {triageIdx >= 0 && triageIdx < overdueAll.length && (
          <TriageWalkthrough
            overdue={overdueAll}
            index={triageIdx}
            onAdvance={() => setTriageIdx(i => i + 1)}
            onExit={() => setTriageIdx(-1)}
            updateReminder={updateReminder}
            deactivateReminder={deactivateReminder}
            deleteReminder={deleteReminder}
            pushToast={pushToast}
          />
        )}
        {triageIdx >= overdueAll.length && triageIdx >= 0 && (() => {
          // All done — auto-exit on next render so we don't get stuck.
          setTimeout(() => setTriageIdx(-1), 0);
          return null;
        })()}
      </div>
      {/* Reminder edit popover — rendered as a sibling of the panel
          (still inside the fixed full-viewport overlay so it stays
          on top of canvas chrome) so it can position itself anywhere
          in the viewport, not just inside the right-side panel. */}
      {editingReminder && (
        <ReminderEditPopover
          reminder={editingReminder}
          defaultReminderTime={defaultReminderTime}
          sourceList={editingSourceList}
          notes={editingSourceList ? lookupNotesForReminder(editingReminder, [editingSourceList]) : []}
          anchorRect={editingAnchorRect}
          onUpdate={updateReminder}
          onUpdateNotes={updateNotes}
          onJump={onJumpToReminder ? () => {
            onJumpToReminder(editingReminder);
            setEditingReminderId(null);
          } : null}
          onClose={() => setEditingReminderId(null)}
          // Walter 2026-05-13 (s1): full row-action set inside the
          // popover. Each callback closes the popover after firing
          // so the user gets the same one-tap UX as the agenda row.
          // Walter 2026-05-13 (round 9): "done" on a RECURRING
          // reminder advances parsedDate to the next occurrence and
          // keeps status='open' — matches Google Tasks / iOS
          // Reminders semantics ("did this one, see you next week").
          // One-off reminders still set status='done'. The popover
          // also exposes a separate "stop reminding" action (below)
          // that kills the rule entirely.
          onMarkDone={() => {
            const rec = editingReminder.recurrence;
            const prevDate = editingReminder.parsedDate?.toDate?.() || editingReminder.parsedDate || null;
            // Walter 2026-05-28 (s1): mark-done semantics differ based
            // on whether there's a pending (fired-but-not-ack'd)
            // instance. The cron sets pendingInstanceAt + advances
            // parsedDate on each fire, so post-fire done just clears
            // pendingInstanceAt + missedCount (parsedDate already
            // points at the next scheduled instance). Pre-fire ("did
            // this one early") done still advances parsedDate
            // ourselves, matching the legacy behavior. Non-recurring
            // unchanged — status='done' kills the row.
            if (rec) {
              const prevPending = editingReminder.pendingInstanceAt;
              const prevMissed = editingReminder.missedCount || 0;
              if (prevPending) {
                // Post-fire done — clear pending + missed. parsedDate
                // already points at next scheduled instance (cron
                // advanced it on fire).
                updateReminder?.(editingReminder.id, {
                  pendingInstanceAt: null,
                  missedCount: 0,
                });
                const nextDateLabel = prevDate
                  ? formatRowDate(prevDate).replace(/:$/, '')
                  : 'next';
                pushToast?.(
                  `did it · next ${nextDateLabel}`,
                  () => updateReminder?.(editingReminder.id, {
                    pendingInstanceAt: prevPending,
                    missedCount: prevMissed,
                  }),
                );
              } else {
                // Pre-fire done — advance parsedDate to next
                // occurrence ourselves.
                const next = computeNextOccurrence(prevDate || new Date(), rec);
                if (next instanceof Date && !isNaN(next.getTime())) {
                  updateReminder?.(editingReminder.id, { parsedDate: next });
                  const dateLabel = formatRowDate(next).replace(/:$/, '');
                  pushToast?.(
                    `did it · next ${dateLabel}`,
                    () => updateReminder?.(editingReminder.id, { parsedDate: prevDate }),
                  );
                } else {
                  updateReminder?.(editingReminder.id, { status: 'done' });
                  pushToast?.('marked done', () => updateReminder?.(editingReminder.id, { status: 'open' }));
                }
              }
            } else {
              updateReminder?.(editingReminder.id, { status: 'done' });
              pushToast?.('marked done', () => updateReminder?.(editingReminder.id, { status: 'open' }));
            }
            setEditingReminderId(null);
          }}
          // Walter 2026-05-13 (round 9): explicit kill-the-rule
          // action for recurring reminders. Sets status='done'
          // unconditionally so the cloud-functions fire engine stops
          // generating new instances. Visible only on recurring
          // reminders (the popover hides it for one-offs).
          onStopReminding={editingReminder.recurrence ? () => {
            updateReminder?.(editingReminder.id, { status: 'done' });
            pushToast?.(
              'stop reminding · won\'t fire again',
              () => updateReminder?.(editingReminder.id, { status: 'open' }),
            );
            setEditingReminderId(null);
          } : null}
          onSnooze={() => {
            // ONE snooze rule everywhere (Walter 2026-08-24): push out a day
            // from today OR from the reminder's own date, whichever is later,
            // at its original time-of-day. lib/snoozeDate.
            const orig = editingReminder.parsedDate
              ? (editingReminder.parsedDate?.toDate?.() || new Date(editingReminder.parsedDate))
              : null;
            const next = snoozeToDays(orig, 1);
            const prev = editingReminder.parsedDate?.toDate?.() || editingReminder.parsedDate || null;
            updateReminder?.(editingReminder.id, { parsedDate: next });
            pushToast?.(`snoozed → ${formatSnoozeLabel(next)}`,
              () => updateReminder?.(editingReminder.id, { parsedDate: prev }),
              { ttlMs: REMINDER_TOAST_TTL_MS });
            setEditingReminderId(null);
          }}
          onRemoveDate={() => {
            const prev = editingReminder.parsedDate?.toDate?.() || editingReminder.parsedDate || null;
            updateReminder?.(editingReminder.id, { parsedDate: null });
            pushToast?.('date removed', () => updateReminder?.(editingReminder.id, { parsedDate: prev }));
            setEditingReminderId(null);
          }}
          onDeactivate={() => {
            deactivateReminder?.(editingReminder.id);
            setEditingReminderId(null);
          }}
          onDelete={() => {
            deleteReminder?.(editingReminder.id);
            setEditingReminderId(null);
          }}
        />
      )}
    </div>
  );
}

function OverdueTriageBanner({ count, onProcess, onHideUntilTomorrow, onPushAllOneWeek, onMakeAllUndated }) {
  const [confirming, setConfirming] = useState(null); // null | 'push' | 'undate'
  // Walter 2026-05-06 round 6: always stack the headline above the
  // action buttons. Round 5 only stacked on mobile, but with the
  // new "hide until tomorrow" button there are now 4 actions, and
  // even on a 560px-wide desktop agenda panel the row layout was
  // squeezing the headline into a 70px-wide column where it
  // wrapped word-by-word. Stacked layout reads cleanly at every
  // width and matches the mobile path the round-5 fix used.
  return (
    <div style={{
      padding: '12px 18px',
      background: 'var(--surface2)',
      borderBottom: '1px solid var(--accent-dim)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '10px',
    }}>
      <span style={{
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        color: 'var(--accent)',
      }}>
        {count} overdue · process the pile?
      </span>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
      }}>
        <button
          onClick={onProcess}
          style={{
            background: 'var(--accent)',
            color: 'var(--bg)',
            border: '1px solid var(--accent)',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            fontWeight: 600,
            padding: '5px 12px',
            cursor: 'pointer',
          }}
        >
          process →
        </button>
        {!confirming ? (
          <>
            {onHideUntilTomorrow && (
              <button
                onClick={onHideUntilTomorrow}
                style={triageBulkBtnStyle}
                title="hide overdue items until tomorrow — does not change their dates"
              >
                hide until tomorrow
              </button>
            )}
            <button
              onClick={() => setConfirming('push')}
              style={triageBulkBtnStyle}
            >
              push all 1wk
            </button>
            <button
              onClick={() => setConfirming('undate')}
              style={triageBulkBtnStyle}
            >
              make all undated
            </button>
          </>
        ) : (
          <>
            <span style={{
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dim)',
            }}>
              {confirming === 'push' ? 'push all forward 1 week?' : 'remove dates from all overdue?'}
            </span>
            <button
              onClick={() => {
                if (confirming === 'push') onPushAllOneWeek?.();
                else onMakeAllUndated?.();
                setConfirming(null);
              }}
              style={{ ...triageBulkBtnStyle, color: 'var(--accent)', borderColor: 'var(--accent-dim)' }}
            >
              yes
            </button>
            <button onClick={() => setConfirming(null)} style={triageBulkBtnStyle}>
              cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const triageBulkBtnStyle = {
  background: 'transparent',
  color: 'var(--text-dim)',
  border: '1px solid var(--border-bright)',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(10px * var(--ui-scale, 1))',
  padding: '5px 10px',
  cursor: 'pointer',
};

function TriageWalkthrough({ overdue, index, onAdvance, onExit, updateReminder, deactivateReminder, deleteReminder, pushToast }) {
  const r = overdue[index];
  if (!r) return null;
  const total = overdue.length;
  const fireD = fireDate(r.parsedDate);

  const snoozeDays = (days) => {
    // ONE snooze rule everywhere (Walter 2026-08-24) — lib/snoozeDate. This
    // panel only ever handles OVERDUE items, so it always lands on today+N;
    // the future-date branch of the rule can't be reached from here.
    const next = snoozeToDays(fireD, days);
    const prevDate = fireD || null;
    updateReminder?.(r.id, { parsedDate: next });
    // Walter 2026-05-18: show the resulting time in a toast so the
    // user can verify what got set. Removes ambiguity when stepping
    // through multiple overdue items rapidly.
    if (pushToast) {
      pushToast(`snoozed → ${formatSnoozeLabel(next)}`,
        () => updateReminder?.(r.id, { parsedDate: prevDate }),
        { ttlMs: REMINDER_TOAST_TTL_MS });
    }
    onAdvance();
  };

  const markDone = () => {
    // Walter 2026-05-13 (round 9): see the onMarkDone wiring at the
    // popover render for the rationale. Recurring → advance to next
    // occurrence (keep status open). One-off → status done.
    const rec = r.recurrence;
    const prevDate = r.parsedDate?.toDate?.() || r.parsedDate || null;
    const next = rec ? computeNextOccurrence(prevDate || new Date(), rec) : null;
    if (rec && next instanceof Date && !isNaN(next.getTime())) {
      updateReminder?.(r.id, { parsedDate: next });
    } else {
      updateReminder?.(r.id, { status: 'done' });
    }
    onAdvance();
  };

  const deactivate = () => {
    (deactivateReminder || deleteReminder)?.(r.id);
    onAdvance();
  };

  const fullDelete = () => {
    deleteReminder?.(r.id);
    onAdvance();
  };

  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      animation: 'lists-fadein 0.15s ease',
    }}>
      <div style={{
        padding: '14px 18px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: '10px',
        fontFamily: 'var(--mono)',
      }}>
        <span style={{ color: 'var(--accent)' }}>processing overdue</span>
        <span style={{ color: 'var(--text-dimmer)', fontSize: 'calc(11px * var(--ui-scale, 1))' }}>
          {index + 1} of {total}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={onExit}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-dim)', padding: '4px',
            fontFamily: 'var(--mono)', fontSize: 'calc(10px * var(--ui-scale, 1))',
          }}
        >
          exit
        </button>
      </div>

      <div style={{
        flex: 1,
        padding: '32px 24px',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: '24px',
      }}>
        <div style={{
          color: 'var(--danger, #d04a4a)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
        }}>
          {fireD ? formatRowDate(fireD) : 'no date'}
        </div>
        <div style={{
          color: 'var(--text)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(15px * var(--ui-scale, 1))',
          textAlign: 'center',
          maxWidth: '440px',
          wordBreak: 'break-word',
          lineHeight: 1.5,
        }}>
          {r.text || <span style={{ color: 'var(--text-dimmer)', fontStyle: 'italic' }}>(empty)</span>}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '8px',
          width: '100%',
          maxWidth: '360px',
          marginTop: '8px',
        }}>
          <TriageBtn onClick={() => snoozeDays(1)}>snooze 1 day</TriageBtn>
          <TriageBtn onClick={() => snoozeDays(7)}>snooze 1 week</TriageBtn>
          <TriageBtn onClick={markDone} accent>mark done</TriageBtn>
          <TriageBtn onClick={deactivate}>deactivate</TriageBtn>
          <TriageBtn onClick={fullDelete} danger>delete line</TriageBtn>
          <TriageBtn onClick={onAdvance}>skip →</TriageBtn>
        </div>
      </div>
    </div>
  );
}

function TriageBtn({ children, onClick, accent, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: accent ? 'var(--accent)' : 'transparent',
        color: accent ? 'var(--bg)' : (danger ? 'var(--danger, #d04a4a)' : 'var(--text)'),
        border: '1px solid ' + (accent ? 'var(--accent)' : (danger ? 'var(--danger, #d04a4a)' : 'var(--border-bright)')),
        borderRadius: 'var(--radius)',
        padding: '10px 14px',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        fontWeight: accent ? 600 : 400,
        cursor: 'pointer',
        transition: 'background 0.12s',
      }}
    >
      {children}
    </button>
  );
}

function Section({ title, accent, children }) {
  return (
    <div style={{ padding: '6px 0' }}>
      <div style={{
        padding: '8px 18px 4px',
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: accent ? 'var(--accent)' : 'var(--text-dimmer)',
      }}>
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ReminderRow({ reminder, notes, updateReminder, deactivateReminder, deleteReminder, updateNotes, pushToast, onOpenSourceEditor, atCaret = false }) {
  // Find hits for THIS row, published by the find bar (lib/agendaFind):
  // { text: {ranges, current}, notes: {ranges, current} } | null.
  const [findHits, setFindHits] = useState(null);
  useEffect(() => subscribeAgendaFind((st) => {
    setFindHits(st.byRow.get(reminder.id) || null);
  }), [reminder.id]);
  const textHits = findHits?.text;
  const notesHits = findHits?.notes;
  const hasNotesHit = (notesHits?.ranges?.length || 0) > 0;
  const rowRef = React.useRef(null);
  const [notesOpen, setNotesOpen] = useState(false);
  // Walter 2026-08-24 ("2: in"): a search hit inside COLLAPSED notes would be
  // counted but invisible — the exact complaint that started the find thread.
  // So a row opens its own notes when they hold a hit, and closes them again
  // when the search moves on (unless the user has started typing in them).
  const [notesOpenedByFind, setNotesOpenedByFind] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState(joinNotes(notes));
  useEffect(() => {
    setNotesDraft(joinNotes(notes));
  }, [notes]);
  // Open this row's notes while they hold a search hit, and close them again
  // when the search moves on (unless the user started typing in them). Must
  // live BELOW the notes state it reads: a dependency array is evaluated
  // during render, so referencing `notesOpen` above its own `const` threw
  // "Cannot access before initialization" and took the whole board down —
  // Walter hit it the moment he opened the agenda (2026-08-24).
  useEffect(() => {
    if (hasNotesHit && !notesOpen) { setNotesOpen(true); setNotesOpenedByFind(true); }
    else if (!hasNotesHit && notesOpenedByFind && !notesEditing) {
      setNotesOpen(false); setNotesOpenedByFind(false);
    }
    if (!notesOpen && notesEditing) setNotesEditing(false);
  }, [hasNotesHit, notesOpen, notesOpenedByFind, notesEditing]);
  const saveNotes = () => {
    if (updateNotes) {
      updateNotes(reminder, notesDraft);
    }
    setNotesOpen(false);
  };
  const [editingText, setEditingText] = useState(false);
  const [textDraft, setTextDraft] = useState(reminder.text || '');

  useEffect(() => {
    setTextDraft(reminder.text || '');
  }, [reminder.text, reminder.parsedDate]);

  const isDone = reminder.status === 'done';
  const isOverdue = reminder.parsedDate && fireDate(reminder.parsedDate) < new Date() && !isDone;

  const saveText = () => {
    const trimmed = textDraft.trim();
    if (trimmed && trimmed !== reminder.text) {
      updateReminder?.(reminder.id, { text: trimmed });
    } else {
      setTextDraft(reminder.text || '');
    }
    setEditingText(false);
  };

  // Snooze rule: lib/snoozeDate (today or the reminder's own date, whichever
  // is later, plus N — at its original time-of-day).
  //
  // Walter 2026-06-02: snooze used to write parsedDate immediately, so
  // the row re-sorted and vanished the instant you tapped — no feedback.
  // Walter snoozed, thought it hadn't taken (row gone), and tapped again,
  // double-snoozing. Now: (c) tapping enters a ~1.2s "held" state that
  // ignores repeat taps, and (a) the row stays IN PLACE showing
  // "snoozed → <date> · undo" during the hold, THEN writes (which is
  // what makes it reflow). Mirrors the mark-done fade pattern above.
  const [snoozing, setSnoozing] = useState(null); // { next, prev, label } | null
  // Walter 2026-06-02 (animation #1): after the hold, collapse the held
  // row (height → 0 + fade) so it leaves its old spot smoothly instead of
  // snapping, THEN write (which reflows it into its new slot).
  const [snoozeCollapsing, setSnoozeCollapsing] = useState(false);
  const snoozeTimerRef = React.useRef(null);
  const snooze = () => {
    if (fadingOut || snoozing) return; // (c) guard against double-fire
    // ONE snooze rule everywhere (Walter 2026-07-19): today+1 at the
    // reminder's original time-of-day — overdue AND future alike (his June
    // split behavior — future → original+1 — read as inconsistent in real
    // use). lib/snoozeDate.
    const next = snoozeToDays(reminder.parsedDate ? fireDate(reminder.parsedDate) : null, 1);
    const prev = fireDate(reminder.parsedDate);
    const label = formatSnoozeLabel(next);
    setSnoozing({ next, prev, label });
    snoozeTimerRef.current = setTimeout(() => {
      // Begin the collapse animation, then commit after it plays.
      setSnoozeCollapsing(true);
      snoozeTimerRef.current = setTimeout(() => {
        snoozeTimerRef.current = null;
        updateReminder?.(reminder.id, { parsedDate: next });
        // The parsedDate change clears snoozing/collapsing via the reset
        // effect and re-sorts the row to its new slot.
        pushToast?.(`snoozed → ${label}`, () => updateReminder?.(reminder.id, { parsedDate: prev }),
          { ttlMs: REMINDER_TOAST_TTL_MS });
      }, 260); // matches the collapse transition below
    }, 2000); // Walter 2026-07-19: 1.1s read as "barely visible" on mobile —
              // the held row is the feedback your eye is on; 2s is readable
              // while still quick enough for rapid triage.
  };
  const cancelSnooze = () => {
    if (snoozeTimerRef.current) { clearTimeout(snoozeTimerRef.current); snoozeTimerRef.current = null; }
    setSnoozeCollapsing(false);
    setSnoozing(null);
  };

  // Walter 2026-05-13: mark-done plays a ~420ms fade-to-gray
  // transition before the status flip propagates (the row would
  // otherwise vanish instantly, since done items drop out of the
  // open-section filters above). The grayscale filter desaturates
  // every coloured chip; opacity dips to 0.25 so the row clearly
  // reads as "leaving" but isn't gone yet. Marking-open (reverse)
  // stays synchronous — the user is undoing or explicitly
  // resurrecting and expects immediate feedback.
  const [fadingOut, setFadingOut] = useState(false);
  // Walter 2026-05-14: explicit-confirm flow for recurring done.
  // Tapping the checkbox on a recurring reminder no longer fires
  // immediately — instead, the row replaces its content with an
  // inline confirm cell showing the next-instance date the user is
  // about to commit to. Confirm → action; Cancel → restore the row.
  // pendingAdvance carries the proposed next Date (or null when
  // not awaiting confirmation). One-off reminders still act on
  // the first tap (no next instance to acknowledge).
  const [pendingAdvance, setPendingAdvance] = useState(null);
  // Walter 2026-05-13 round 11: reset the fade-out animation state
  // whenever the reminder's status or parsedDate changes. Without
  // this, hitting "did it" on a recurring reminder sets fadingOut
  // true (and the row goes gray), and tapping undo restores the
  // parsedDate but the SAME Row component keeps fadingOut=true —
  // so the row stays grey forever. Same shape would affect a
  // one-off → done → undo cycle if the Row re-mounts in the same
  // section. Reset whenever the underlying data changes; that's
  // the signal that the previous action either committed or was
  // undone. Also clears any pending advance prompt.
  useEffect(() => {
    setFadingOut(false);
    setPendingAdvance(null);
    // The snooze write landed (parsedDate changed) → drop the held state
    // so the row reflows. Also clear any lingering timer defensively.
    if (snoozeTimerRef.current) { clearTimeout(snoozeTimerRef.current); snoozeTimerRef.current = null; }
    setSnoozing(null);
    setSnoozeCollapsing(false);
  }, [reminder.status, reminder.parsedDate]);
  const commitAdvance = (nextDate) => {
    const prevDate = reminder.parsedDate?.toDate?.() || reminder.parsedDate || null;
    setPendingAdvance(null);
    setFadingOut(true);
    setTimeout(() => {
      updateReminder?.(reminder.id, { parsedDate: nextDate });
      const dateLabel = formatRowDate(nextDate).replace(/:$/, '');
      pushToast?.(
        `did it · next ${dateLabel}`,
        () => updateReminder?.(reminder.id, { parsedDate: prevDate }),
      );
    }, 420);
  };
  // Walter 2026-05-28 (s1): post-fire mark-done for recurring. The
  // cron set pendingInstanceAt + advanced parsedDate already; we just
  // clear the pending tracking. No new parsedDate write needed.
  const commitClearPending = () => {
    const prevPending = reminder.pendingInstanceAt;
    const prevMissed = reminder.missedCount || 0;
    const nextDate = reminder.parsedDate?.toDate?.() || reminder.parsedDate || null;
    setFadingOut(true);
    setTimeout(() => {
      updateReminder?.(reminder.id, {
        pendingInstanceAt: null,
        missedCount: 0,
      });
      const dateLabel = nextDate
        ? formatRowDate(nextDate).replace(/:$/, '')
        : 'next';
      pushToast?.(
        `did it · next ${dateLabel}`,
        () => updateReminder?.(reminder.id, {
          pendingInstanceAt: prevPending,
          missedCount: prevMissed,
        }),
      );
    }, 420);
  };
  const toggleDone = () => {
    if (!isDone) {
      // Walter 2026-05-14 (round 12): if there's a recurrence, show
      // the explicit-confirm cell with the proposed next date. The
      // user has to acknowledge before the advance commits. One-off
      // reminders still act immediately on tap.
      // Walter 2026-05-28 (s1): when there's a pendingInstanceAt set
      // (post-fire state), the user is ack'ing the fired instance.
      // The cron already advanced parsedDate, so just clear pending.
      // No confirm cell needed — the action is "ack what fired," and
      // the next-instance preview is already visible inline in the
      // row's date annotation (no surprise to acknowledge).
      const rec = reminder.recurrence;
      if (rec && reminder.pendingInstanceAt) {
        commitClearPending();
        return;
      }
      const prevDate = reminder.parsedDate?.toDate?.() || reminder.parsedDate || null;
      const next = rec ? computeNextOccurrence(prevDate || new Date(), rec) : null;
      const isAdvance = !!(rec && next instanceof Date && !isNaN(next.getTime()));
      if (isAdvance) {
        setPendingAdvance(next);
        return;
      }
      // One-off path (or recurring rule we don't know how to
      // advance, e.g. bySetPos) — same immediate-fade behaviour
      // as before.
      setFadingOut(true);
      setTimeout(() => {
        updateReminder?.(reminder.id, { status: 'done' });
        pushToast?.(
          'marked done',
          () => updateReminder?.(reminder.id, { status: 'open' }),
        );
      }, 420);
      return;
    }
    updateReminder?.(reminder.id, { status: 'open' });
    pushToast?.(
      'marked open',
      () => updateReminder?.(reminder.id, { status: 'done' }),
    );
  };

  // Walter 2026-05-14 (round 12): when the user taps the checkbox
  // on a recurring reminder we don't immediately advance — we show
  // an explicit-confirm cell with the proposed next instance. The
  // cell replaces the row content so the user has to acknowledge
  // before the change commits. No auto-confirm; the user must tap.
  // Walter 2026-06-02: snooze hold cell (a). While held, the row stays in
  // place showing "snoozed → <date>" with an undo, so the action is visibly
  // acknowledged before it reflows ~1.2s later.
  //
  // Walter 2026-08-24: the confirmation used to REPLACE the row — *"it
  // replaces the entry I'm snoozing rather than appearing underneath it"*.
  // Acknowledging an action by hiding the thing you acted on is backwards:
  // you lose sight of which reminder it was at exactly the moment you might
  // want to undo. It now sits BENEATH the row, which stays put and readable.
  const snoozeBanner = snoozing ? (
      <div
        style={{
          // Collapse the banner (height → 0 + fade + padding → 0) when the
          // hold ends, so it tidies itself away just before the write
          // reflows the row. Walter 2026-06-02 animation #1.
          display: 'flex', alignItems: 'center', gap: 12,
          borderTop: '1px solid var(--border)',
          background: 'var(--accent-wash, rgba(196,81,30,0.10))',
          fontSize: 'calc(12px * var(--ui-scale, 1))',
          overflow: 'hidden',
          maxHeight: snoozeCollapsing ? 0 : 60,
          opacity: snoozeCollapsing ? 0 : 0.9,
          paddingTop: snoozeCollapsing ? 0 : 10,
          paddingBottom: snoozeCollapsing ? 0 : 10,
          paddingLeft: 18,
          paddingRight: 18,
          transition: 'max-height 0.26s ease, opacity 0.22s ease, padding 0.26s ease',
        }}
      >
        <span aria-hidden="true" style={{ flexShrink: 0 }}>🕘</span>
        <div style={{ flex: 1, minWidth: 0, color: 'var(--text)' }}>
          snoozed → <span style={{ fontWeight: 600 }}>{snoozing.label}</span>
        </div>
        <button
          onClick={cancelSnooze}
          title="undo snooze"
          style={{
            background: 'transparent', color: 'var(--text-dim)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            padding: '5px 12px', fontFamily: 'var(--mono)',
            fontSize: 'inherit', cursor: 'pointer', flexShrink: 0,
          }}
        >
          undo
        </button>
      </div>
  ) : null;

  if (pendingAdvance) {
    const currentLabel = reminder.parsedDate
      ? formatRowDate(reminder.parsedDate).replace(/:$/, '')
      : 'this one';
    const nextLabel = formatRowDate(pendingAdvance).replace(/:$/, '');
    return (
      <div
        ref={rowRef}
        style={{
          padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 12,
          borderTop: '1px solid var(--border)',
          background: 'var(--accent-wash, rgba(196,81,30,0.10))',
          fontSize: 'calc(12px * var(--ui-scale, 1))',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: 'var(--text)', fontWeight: 600, marginBottom: 2 }}>
            {currentLabel} done
          </div>
          <div style={{ color: 'var(--text-dim)' }}>
            next instance: <span style={{ color: 'var(--text)', fontWeight: 600 }}>{nextLabel}</span>
          </div>
        </div>
        <button
          onClick={() => setPendingAdvance(null)}
          title="cancel — leave the reminder where it is"
          style={{
            background: 'transparent',
            color: 'var(--text-dim)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '6px 12px',
            fontFamily: 'var(--mono)',
            fontSize: 'inherit',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          cancel
        </button>
        <button
          onClick={() => commitAdvance(pendingAdvance)}
          title="confirm — advance to the next instance"
          style={{
            background: 'var(--accent)',
            color: 'var(--bg)',
            border: 'none',
            borderRadius: 'var(--radius)',
            padding: '6px 14px',
            fontFamily: 'var(--mono)',
            fontSize: 'inherit',
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          confirm
        </button>
      </div>
    );
  }

  return (
    <>
    <div
      ref={(el) => { rowRef.current = el; registerAgendaRowEl(reminder.id, el); }}
      style={{
        padding: '10px 18px',
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        borderTop: '1px solid var(--border)',
        // While the snooze confirmation is showing beneath it, dim the row
        // slightly — it's on its way out, and this reads as "this one".
        opacity: fadingOut ? 0.25 : (snoozing ? 0.55 : (isDone ? 0.5 : 1)),
        filter: fadingOut ? 'grayscale(1)' : 'none',
        transition: 'opacity 0.42s ease, filter 0.42s ease, background 0.45s ease',
        // The same bell-gold wash the editor puts on a reminder's date, so
        // "this is the line your cursor is on" reads as one idea with the
        // source line (Walter 2026-10-09). Stays while the caret stays.
        background: atCaret ? 'var(--syntax-reminder-tint)' : 'transparent',
        fontSize: 'calc(12px * var(--ui-scale, 1))',
      }}
    >
      <button
        onClick={toggleDone}
        title={isDone ? 'mark open' : 'mark done'}
        style={{
          width: 16, height: 16, marginTop: 2,
          borderRadius: '3px',
          border: '1px solid ' + (isDone ? 'var(--accent)' : 'var(--border-bright)'),
          background: isDone ? 'var(--accent)' : 'transparent',
          cursor: 'pointer', flex: 'none',
          padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {/* Walter 2026-05-06 round 5: an empty box wasn't reading
            as "checkbox / mark done". A faint gray check inside the
            unchecked state telegraphs the action without competing
            with the orange-filled "done" state. */}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path
            d="M2 5l2 2 4-4"
            stroke={isDone ? 'var(--bg)' : 'var(--border-bright)'}
            strokeWidth={isDone ? 1.6 : 1.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={isDone ? 1 : 0.6}
          />
        </svg>
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Recurrence + date row. Walter 2026-05-12 (Q1/Q3 reply):
            recurrence rule shown ONCE as an editable chip; the
            "next instance" date is shown separately + non-editable
            on the line below for recurring reminders. The leading ↻
            glyph is back as a small eye-guide (Walter's Q3 — wants
            visual cues for "this is recurring", but not editorial
            content). Non-recurring reminders use the date chip as
            their only chip (no separate next-instance line needed —
            the date IS the canonical form). */}
        {(() => {
          const rec = reminder.recurrence ? normalizeRecurrence(reminder.recurrence) : null;
          const recLabel = rec ? formatRecurrenceLabel(rec, { omitEndCondition: true }) : '';
          // Walter 2026-05-28 (s1): for recurring reminders, prefer
          // `pendingInstanceAt` over `parsedDate` when set. The cron
          // sets pendingInstanceAt whenever a recurring fires, and
          // clears it on user mark-done. Showing the pending instance
          // keeps the FIRED time visible until the user acts on it,
          // even after parsedDate has advanced to the next occurrence.
          // For non-recurring there's no pending field — falls back to
          // parsedDate.
          const pendingFired = rec ? fireDate(reminder.pendingInstanceAt) : null;
          const displayDate = pendingFired || reminder.parsedDate;
          const dateLabel = displayDate ? formatRowDate(displayDate) : 'no date';
          // Walter 2026-05-28 (s1): N-missed counter — option (b).
          // missedCount is the number of PRIOR fires that went un-ack'd
          // before the current pending one. Total instances pending =
          // missedCount + 1. Show only when > 1 to avoid noise.
          const totalMissed = pendingFired ? ((reminder.missedCount || 0) + 1) : 0;
          const openEditor = () => {
            const rect = rowRef.current?.getBoundingClientRect?.();
            onOpenSourceEditor?.(rect ? {
              top: rect.top, left: rect.left,
              right: rect.right, bottom: rect.bottom,
              width: rect.width, height: rect.height,
            } : null);
          };
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                {/* ↻ leading icon — eye-guide for recurring rows.
                    Not clickable; the chip next to it is the edit
                    affordance. Walter's Q3: the icon helps you scan
                    the agenda for repeating items at a glance. */}
                {rec && recLabel && (
                  <span
                    aria-hidden="true"
                    style={{
                      color: 'var(--accent)',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(14px * var(--ui-scale, 1))',
                      lineHeight: 1,
                      flex: 'none',
                    }}
                  >↻</span>
                )}
                {/* Editable chip: recurrence rule when set, otherwise
                    the date itself. Click → open the edit popover
                    (the only surface where rule + date + notes all
                    live together). */}
                <button
                  onClick={openEditor}
                  title={rec ? `${recLabel} — click to edit` : 'edit reminder'}
                  style={{
                    background: 'transparent',
                    border: '1px solid ' + (isOverdue && !rec ? 'var(--danger, #d04a4a)' : 'var(--border)'),
                    borderRadius: 'var(--radius)',
                    padding: '2px 8px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    color: isOverdue && !rec
                      ? 'var(--danger, #d04a4a)'
                      : (rec || reminder.parsedDate ? 'var(--accent)' : 'var(--text-dimmer)'),
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {rec && recLabel ? recLabel : dateLabel}
                </button>
              </div>
              {/* Non-editable date annotation line for recurring
                  reminders. Walter 2026-05-28 (s1): when there's a
                  pending (fired-but-not-ack'd) instance, show THAT
                  date with the "N missed" counter (option b). When
                  there's no pending instance, show the upcoming
                  scheduled "next instance:" as before. The pending
                  case implicitly indicates overdue — we don't need to
                  also say "overdue:" since the date itself reads as
                  past. */}
              {rec && pendingFired && (
                <>
                  {/* The fired-but-unacknowledged instance, made
                      explicit (Walter 2026-06-02, option B): even a
                      single un-acked fire now says "not done yet" so the
                      past date isn't a mystery, and we ALSO show the next
                      upcoming instance below so the agenda agrees with
                      the edit popover instead of contradicting it. */}
                  <div style={{
                    marginBottom: '4px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(10px * var(--ui-scale, 1))',
                    color: 'var(--danger, #d04a4a)',
                  }}>
                    {dateLabel.replace(/:$/, '')} · not done yet
                    {totalMissed > 1 && (
                      <span style={{ marginLeft: 6, opacity: 0.85 }}>
                        · {totalMissed} missed
                      </span>
                    )}
                  </div>
                  {reminder.parsedDate && (
                    <div style={{
                      marginBottom: '4px',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(10px * var(--ui-scale, 1))',
                      color: 'var(--text-dim)',
                    }}>
                      next instance: {formatRowDate(reminder.parsedDate).replace(/:$/, '')}
                    </div>
                  )}
                </>
              )}
              {rec && !pendingFired && reminder.parsedDate && (
                <div style={{
                  marginBottom: '4px',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  color: isOverdue ? 'var(--danger, #d04a4a)' : 'var(--text-dim)',
                }}>
                  next instance: {dateLabel.replace(/:$/, '')}
                </div>
              )}
            </>
          );
        })()}

        {/* Text — Walter 2026-05-06 round 8: textarea instead of a
            single-line input so long titles wrap and the editor
            shows what's actually there. Auto-grows up to 8 rows;
            Enter still saves (Shift+Enter inserts a newline, Esc
            cancels) so single-tap save still works for short
            titles. Padding bumped from 4px/8px to 8px/10px so the
            field reads as a deliberate edit surface, not an inline
            sliver. */}
        {editingText ? (
          <textarea
            value={textDraft}
            onChange={e => setTextDraft(e.target.value)}
            onBlur={saveText}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveText(); }
              else if (e.key === 'Escape') { setTextDraft(reminder.text || ''); setEditingText(false); }
            }}
            autoFocus
            rows={Math.max(2, Math.min(8, (textDraft || '').split('\n').length + Math.floor((textDraft || '').length / 50)))}
            style={{
              background: 'var(--surface)', color: 'var(--text)',
              border: '1px solid var(--accent-dim)',
              borderRadius: 'var(--radius)',
              padding: '8px 10px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(12px * var(--ui-scale, 1))',
              lineHeight: 1.45,
              width: '100%',
              minHeight: 56,
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
              display: 'block',
            }}
          />
        ) : (
          <div
            onClick={() => setEditingText(true)}
            style={{
              cursor: 'text',
              wordBreak: 'break-word',
              textDecoration: isDone ? 'line-through' : 'none',
              color: isDone ? 'var(--text-dimmer)' : 'var(--text)',
            }}
            title="click to edit"
          >
            {reminder.text
              ? <HighlightedText text={reminder.text} ranges={textHits?.ranges} current={textHits?.current ?? -1} />
              : <span style={{ color: 'var(--text-dimmer)', fontStyle: 'italic' }}>(empty)</span>}
          </div>
        )}

        {/* Attached notes — the continuation lines under the 🔔 line
            in the source list. Closed: shows "+ N notes" or "+ add
            note" affordance. Open: textarea bound to draft, saves on
            blur (writes back to the source list as `- ` lines). */}
        <div style={{ marginTop: '6px' }}>
          {!notesOpen ? (
            <button
              onClick={() => setNotesOpen(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-dimmer)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(10px * var(--ui-scale, 1))',
                cursor: 'pointer',
                padding: '2px 0',
                textAlign: 'left',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {/* Walter 2026-05-06 round 5: instead of the bare count
                  ("+ 3 notes"), show the start of the first note so
                  the user can recall what's there at a glance. The
                  full body lives in the open editor. */}
              {notes && notes.length > 0
                ? renderNotesPreview(notes)
                : '+ add note'}
            </button>
          ) : (
            <div style={{
              marginTop: '2px',
              padding: '6px 8px',
              background: 'var(--surface2)',
              borderLeft: '2px solid var(--border)',
              borderRadius: '0 var(--radius) var(--radius) 0',
            }}>
              {(hasNotesHit || notesOpenedByFind) && !notesEditing ? (
                // Find opened these notes. Show them as READ-ONLY highlighted
                // text rather than the editor: the textarea autoFocuses, which
                // would rip the keyboard away from the find box mid-search,
                // and a textarea can't tint a substring anyway. Clicking hands
                // over to the real editor.
                //
                // The `notesOpenedByFind` half matters (Walter 2026-08-24:
                // "the search bar will stop accepting inputs after the second
                // letter"). Typing a letter that drops this row's last hit
                // leaves ONE render where the notes are still open but the hit
                // is gone — long enough for the textarea to mount, autoFocus,
                // and swallow every keystroke after it. Staying on the
                // read-only view until the notes actually close closes that
                // window.
                <div
                  onClick={() => setNotesEditing(true)}
                  title="click to edit"
                  style={{
                    width: '100%',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    border: '1px solid var(--accent-dim)',
                    borderRadius: 'var(--radius)',
                    padding: '6px 8px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    boxSizing: 'border-box',
                    minHeight: 80,
                    cursor: 'text',
                  }}
                >
                  <HighlightedText
                    text={joinNotes(notes)}
                    ranges={notesHits?.ranges}
                    current={notesHits?.current ?? -1}
                  />
                </div>
              ) : (
              <>
              <textarea
                value={notesDraft}
                onChange={e => setNotesDraft(e.target.value)}
                onBlur={saveNotes}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setNotesDraft(joinNotes(notes));
                    setNotesOpen(false);
                  }
                }}
                placeholder="add notes…"
                // Belt and braces on the same bug: only grab focus when the
                // USER opened these notes (or clicked into ones find opened).
                autoFocus={notesEditing || !notesOpenedByFind}
                // Walter 2026-05-06 round 6: bumped the floor from 2
                // to 4 rows so the editor reads as a proper notes
                // surface even when empty (the previous 2-row default
                // was visually cramped — looked like a single line
                // with content already in it).
                rows={Math.max(4, Math.min(12, notesDraft.split('\n').length + 2))}
                style={{
                  width: '100%',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  border: '1px solid var(--accent-dim)',
                  borderRadius: 'var(--radius)',
                  padding: '6px 8px',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  lineHeight: 1.5,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                  minHeight: 80,
                  // Cap rendered height for long notes so the row
                  // doesn't dominate the panel; scroll within the
                  // textarea past 12 lines.
                  maxHeight: '40vh',
                  overflowY: 'auto',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={saveNotes}
                  style={{
                    background: 'var(--accent)',
                    color: 'var(--bg)',
                    border: '1px solid var(--accent)',
                    borderRadius: 'var(--radius)',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(10px * var(--ui-scale, 1))',
                    fontWeight: 600,
                    padding: '4px 12px',
                    cursor: 'pointer',
                  }}
                >
                  save
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setNotesDraft(joinNotes(notes)); setNotesOpen(false); }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-bright)',
                    color: 'var(--text-dim)',
                    borderRadius: 'var(--radius)',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(10px * var(--ui-scale, 1))',
                    padding: '4px 12px',
                    cursor: 'pointer',
                  }}
                >
                  cancel
                </button>
                <span style={{
                  color: 'var(--text-dimmer)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                }}>
                  Esc to cancel · click outside to save
                </span>
              </div>
              </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions — Walter 2026-05-06 round 5: edit/source button is
          now leftmost + slightly wider than the others, since it's
          the most-used action on a row. */}
      <div style={{ display: 'flex', gap: '4px', flex: 'none' }}>
        {onOpenSourceEditor && (
          <RowAction
            onClick={() => {
              const rect = rowRef.current?.getBoundingClientRect?.();
              onOpenSourceEditor(rect ? {
                top: rect.top, left: rect.left,
                right: rect.right, bottom: rect.bottom,
                width: rect.width, height: rect.height,
              } : null);
            }}
            title="see source and edit"
            wide
          >
            <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
              <path d="M5 2H3a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V8" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M8 2h3v3M11 2L6 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </RowAction>
        )}
        <RowAction onClick={snooze} title="snooze 1 day">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M6.5 3v3.5l2 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.4"/>
          </svg>
        </RowAction>
        {/* Remove date — turns a dated reminder into an undated one
            (parsedDate=null). Keeps the bell + the text. Useful when
            something's stuck overdue and you don't want to commit
            to a new date right now. Hidden for already-undated. */}
        {reminder.parsedDate && (
          <RowAction
            onClick={() => updateReminder?.(reminder.id, { parsedDate: null })}
            title="remove date (make undated)"
          >
            <span style={{
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              textDecoration: 'line-through',
              padding: '0 2px',
            }}>
              date
            </span>
          </RowAction>
        )}
        {/* Deactivate: bell with a slash through it. Strips the bell
            from the source line so the text stays put — the user just
            stops getting reminded about it. */}
        <RowAction
          onClick={() => (deactivateReminder || deleteReminder)?.(reminder.id)}
          title="deactivate as reminder but keep text in list"
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <path d="M4 6a4 4 0 0 1 8 0c0 4 2 5 2 5H2s2-1 2-5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
            <path d="M7 13a1.4 1.4 0 0 0 2 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            <path d="M2 14L14 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </RowAction>
        {/* Full delete: trash can. Removes the line from the source
            list entirely. More destructive — labelled distinctly so
            the user has to mean it. */}
        <RowAction
          onClick={() => deleteReminder?.(reminder.id)}
          title="delete (remove line from list)"
          danger
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M2.5 4h8M5 4V2.5h3V4M4 4l.5 7h4L9 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
        </RowAction>
      </div>
    </div>
    {snoozeBanner}
    </>
  );
}

function RowAction({ children, onClick, title, danger, wide }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: 'transparent',
        border: '1px solid ' + (wide ? 'var(--border-bright)' : 'var(--border)'),
        borderRadius: 'var(--radius)',
        color: danger ? 'var(--danger, #d04a4a)' : (wide ? 'var(--text)' : 'var(--text-dim)'),
        // Walter 2026-05-06 round 5: `wide` = the most-used action on
        // a row (today: source/edit). Slightly bigger padding +
        // brighter chrome so it pops vs the secondary icons.
        padding: wide ? '4px 12px' : '4px 6px',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'var(--surface2)';
        e.currentTarget.style.color = danger ? 'var(--danger, #d04a4a)' : 'var(--text)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = danger ? 'var(--danger, #d04a4a)' : 'var(--text-dim)';
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function fireDate(d) {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d.toDate === 'function') return d.toDate(); // Firestore Timestamp
  return new Date(d);
}

function startOfDay(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function dayDiff(a, b) {
  return Math.round((startOfDay(a) - startOfDay(b)) / 86400000);
}

function groupReminders(reminders, filter) {
  const now = new Date();
  const today = startOfDay(now);

  // Compute section IDs once.
  const overdue = [];
  const todayItems = [];
  const tomorrow = [];
  const thisWeek = [];
  const later = [];
  const undated = [];
  const done = [];

  for (const r of reminders) {
    if (r.status === 'done') { done.push(r); continue; }
    const d = fireDate(r.parsedDate);
    if (!d) { undated.push(r); continue; }
    const diff = dayDiff(d, today);
    // Walter 2026-05-06: previous version routed `diff === 0 && d > now`
    // (today-but-still-future) into the "later" bucket because the
    // outer guard `d < now && diff <= 0` was false and none of the
    // diff branches matched 0. Fix: branch on diff explicitly.
    if (diff < 0) { overdue.push(r); continue; }
    if (diff === 0) {
      if (d < now) overdue.push(r); else todayItems.push(r);
      continue;
    }
    if (diff === 1) tomorrow.push(r);
    else if (diff > 1 && diff <= 7) thisWeek.push(r);
    else later.push(r);
  }

  // Sort by trigger date within each dated section.
  const cmp = (a, b) => fireDate(a.parsedDate) - fireDate(b.parsedDate);
  overdue.sort(cmp);
  todayItems.sort(cmp);
  tomorrow.sort(cmp);
  thisWeek.sort(cmp);
  later.sort(cmp);

  let sections = [
    { id: 'overdue',  label: 'overdue',  items: overdue,    accent: true },
    { id: 'today',    label: 'today',    items: todayItems, accent: false },
    { id: 'tomorrow', label: 'tomorrow', items: tomorrow,   accent: false },
    { id: 'week',     label: 'this week', items: thisWeek,  accent: false },
    { id: 'later',    label: 'later',    items: later,      accent: false },
    { id: 'undated',  label: 'no date',  items: undated,    accent: false },
    { id: 'done',     label: 'done',     items: done,       accent: false },
  ];

  // Walter 2026-05-06 round 5: today/week filters now EXCLUDE
  // overdue (those have their own dedicated chip + section). The
  // intuition: if a user picks "today", they want to focus on today's
  // workload, not be stared down by yesterday's misses.
  if (filter === 'overdue') {
    sections = sections.filter(s => s.id === 'overdue');
  } else if (filter === 'today') {
    sections = sections.filter(s => s.id === 'today');
  } else if (filter === 'week') {
    sections = sections.filter(s => ['today', 'tomorrow', 'week'].includes(s.id));
  }

  return sections;
}

function formatRowDate(d) {
  const date = fireDate(d);
  if (!date) return 'no date';
  const today = startOfDay(new Date());
  const diff = dayDiff(date, today);
  const time = formatTimeShort(date);
  // Walter 2026-05-12: agenda chips always end in ":" — the chip is the
  // label for the body line that follows it, so the trailing colon reads
  // as "this is the when, the what is below." Year suffix is inserted
  // between the date and the time when the year differs from current
  // ("May 11 '27 7am:"), so the date/year cluster reads as a unit.
  if (diff === 0) return `today ${time}:`;
  if (diff === 1) return `tomorrow ${time}:`;
  if (diff === -1) return `yesterday ${time}:`;
  // Walter 2026-06-16: numeric "6/22" for every other date — matches the
  // source-list bell line (formatMonthDay). Was a weekday name ("Mon") for
  // within-week dates and a month name ("Jun 22") beyond; both broke the
  // "6/22" convention the list uses, so the same reminder read "6/22" in the
  // list but "Mon" in the agenda. (today/tomorrow/yesterday stay as friendly
  // relative labels — clearer, and not part of the inconsistency.)
  const md = formatMonthDay(date);
  const year = date.getFullYear();
  // Walter 2026-05-28 (s3): when the date is in a different calendar
  // year AND more than ~3 months out, render the FULL four-digit year
  // ("May 12 2027 8am:") instead of the compact "'27" suffix. Walter
  // was missing reminders that the parser had auto-advanced into next
  // year — the apostrophe-suffix form was too easy to skim past. For
  // dates within ~3 months of today that happen to cross a year
  // boundary (Dec → Jan), keep the compact form because those are
  // normal "next month" cases and the full year would feel formal.
  if (year !== today.getFullYear()) {
    const farFuture = date.getTime() - today.getTime() > 90 * 86400000;
    if (farFuture) return `${md} ${year} ${time}:`;
    const yy = String(year).slice(-2);
    return `${md} '${yy} ${time}:`;
  }
  return `${md} ${time}:`;
}

function toLocalDateInput(d) {
  const date = fireDate(d);
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toLocalTimeInput(d) {
  const date = fireDate(d);
  if (!date) return '';
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * lookupNotesForReminder — finds the reminder's 🔔 line in its source
 * list (by sourceLineHash) and returns the array of continuation
 * lines that immediately follow it. A continuation line is one that
 * starts with whitespace, "-", "*", or "·"; the run ends at the
 * first non-continuation line OR a blank line.
 *
 * Returns [] if the source list isn't on the current board (cross-
 * board reminders) or if the bell line can't be located.
 */
// Notes come from the source list as raw line strings. Walter 2026-08-25:
// show them AS THEY ARE. We used to strip a leading "- "/indent for display
// and re-add it on write, which was right when that marker was what MADE a
// line a note — the paragraph rule replaced it, and stripping now would eat a
// marker the user typed on purpose. Legacy notes keep their "- " visible,
// which is honest: it really is in the list text.
function joinNotes(notes) {
  if (!notes || notes.length === 0) return '';
  return notes.join('\n');
}

// Build the closed-state notes label: first non-empty line
// (truncated) + an italic "…more" hint when there's more than just
// that one line. Long single lines also get an ellipsis once past
// PREVIEW_CHARS to keep the row chrome predictable.
// Walter 2026-05-06 round 6: returns React nodes (not a string) so
// the …more affordance can be italic + underlined and visually
// distinct from the note prose.
const PREVIEW_CHARS = 60;
function renderNotesPreview(notes) {
  if (!notes || notes.length === 0) return '+ add note';
  const cleaned = notes.map(l => (l || '').trim()).filter(Boolean);
  if (cleaned.length === 0) return '+ add note';
  const first = cleaned[0];
  const truncated = first.length > PREVIEW_CHARS
    ? first.slice(0, PREVIEW_CHARS).trimEnd() + '…'
    : first;
  const showMore = cleaned.length > 1;
  return (
    <>
      <span>{`· ${truncated}`}</span>
      {showMore && (
        <span style={{
          fontStyle: 'italic',
          textDecoration: 'underline',
          color: 'var(--text-dim)',
          marginLeft: 4,
        }}>
          …more
        </span>
      )}
    </>
  );
}

function lookupNotesForReminder(r, lists) {
  if (!r || !r.sourceListId || !r.sourceLineHash || !lists) return [];
  const sl = lists.find(l => l.id === r.sourceListId);
  if (!sl) return [];
  // Match the bell prefix loosely — with or without trailing
  // whitespace, and either open- or done-state form.
  // Walter 2026-05-11: tolerate the optional ↻ recurrence glyph
  // between 🔔 and the content (formatBellLine inserts it for
  // recurring reminders so the source line reflects translation).
  const BELL_RE_LOOSE = /^(?:~~)?(?:🔔|🔕|\[DONE\])↻?\s?/;
  const linesArr = (sl.content || '').split('\n');
  let bellIdx = -1;
  for (let i = 0; i < linesArr.length; i++) {
    const ln = linesArr[i];
    const m = ln.match(BELL_RE_LOOSE);
    if (!m) continue;
    if (hashReminderLine(ln.slice(m[0].length)) === r.sourceLineHash) {
      bellIdx = i;
      break;
    }
  }
  if (bellIdx < 0) return [];
  const out = [];
  // Walter 2026-08-25: the reminder owns every line under it up to the next
  // blank line — the separator you can already see. Another 🔔/[DONE] line
  // ends the run too, so back-to-back reminders never swallow each other.
  // (The old rule — a leading space or bullet — is a subset of this, so
  // nothing anyone already wrote changes meaning.)
  for (let i = bellIdx + 1; i < linesArr.length; i++) {
    const ln = linesArr[i];
    if (ln.trim() === '') break;
    if (BELL_RE_LOOSE.test(ln)) break;
    out.push(ln);
  }
  return out;
}
